import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { api } from '../services/api';
import { useGetProfileSummary, usePostsPoll } from '../queries/onboardingPolls';
import { parseISO, isWithinInterval, addDays, compareAsc } from 'date-fns';

/**
 * Custom hook to manage dashboard data and logic
 * Centralizes all dashboard state and data fetching
 * @returns {Object} Dashboard data, states, and handlers
 */
export const useDashboard = () => {
  const { accessKey, subscription } = useAppStore();
  const queryClient = useQueryClient();
  // Fetch dashboard data
  const { data: posts = [], isLoading: postsLoading, error: postsError } = usePostsPoll(true);

  const {
    data: socialAccounts = [],
    isLoading: accountsLoading,
    error: accountsError,
  } = useQuery({
    queryKey: ['social-accounts', accessKey],
    queryFn: () => api.fetchSocialAccounts(accessKey),
    enabled: !!accessKey,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useGetProfileSummary(accessKey, true);

  // Loading and error states
  const isLoading = postsLoading || accountsLoading || profileLoading;
  const error = postsError || accountsError || profileError;

  // Calculate stats
  const stats = {
    totalPosts: posts.length,
    scheduledPosts: posts.filter((post) => post.status === 'approved').length,
    pendingPosts: posts.filter((post) => post.status === 'pending').length,
    connectedAccounts: socialAccounts?.length,
    activeAccounts:
      socialAccounts.length > 0
        ? socialAccounts.filter((account) => account.status === 'active').length
        : 0,
  };

  // Get upcoming posts (next 7 days)
  const upcomingPosts = posts
    .filter((post) => post.scheduledAt)
    .filter((post) => {
      const postDate = parseISO(post.scheduledAt);
      const now = new Date();
      const weekFromNow = addDays(now, 7);
      return isWithinInterval(postDate, { start: now, end: weekFromNow });
    })
    .sort((a, b) => compareAsc(parseISO(a.scheduledAt), parseISO(b.scheduledAt)));

  // Determine how many posts to show based on subscription
  const maxVisiblePosts = subscription?.product_name === 'Welcome' || !subscription ? 3 : 4;
  const displayPosts = upcomingPosts.slice(0, maxVisiblePosts);
  const showUpgradeCTA =
    (subscription?.product_name === 'Welcome' || !subscription) && upcomingPosts.length >= 3;

  // Handlers

  return {
    // Data
    posts,
    socialAccounts,
    profile,
    subscription,
    accessKey,

    // Computed data
    stats,
    upcomingPosts,
    displayPosts,
    showUpgradeCTA,

    // Loading and error states
    isLoading,
    error,
    postsLoading,
    accountsLoading,
    profileLoading,
  };
};

export default useDashboard;
